<template>
  <div class="review-tree">
    <div
      v-for="(item, index) in data"
      :key="item.id || index"
      class="tree-item"
      :style="{ paddingLeft: level * 32 + 'px' }"
    >
      <div class="item-wrapper">
        <div class="avatar-container">
          <img v-if="item.userImage" :src="item.userImage" class="user-avatar" alt="用户头像" />
          <div v-else class="user-avatar-placeholder">
            <i class="el-icon-user"></i>
          </div>
        </div>
        <div class="content-wrapper">
          <div class="item-header">
            <span class="user-name">{{ item.userName }}</span>
          </div>
          <div class="item-content">
            <span v-if="item.parentReplyId || item.parentUserId" class="reply-prefix">
              回复@{{ item.parentUserName || parentName || "用户" }}：
            </span>
            <span class="content">{{ item.content }}</span>
          </div>
          <div class="date-row">
            <span class="date">{{ formatDate(item.ctdate) }}</span>
            <span v-if="level > 0" class="delete-btn" @click.stop="handleDelete(item)">
              <i class="el-icon-delete"></i>
              <span>删除</span>
            </span>
          </div>
          <div class="item-actions">
            <span
              v-if="item.reviewReplies && item.reviewReplies.length > 0"
              class="expand-toggle"
              @click.stop="toggleExpand(item)"
            >
              <i
                :class="
                  expandedIds.includes(item.id) ? 'el-icon-arrow-down' : 'el-icon-arrow-right'
                "
              ></i>
              <span>{{ expandedIds.includes(item.id) ? "收起" : "展开" }}</span>
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="level > 0"
        class="tree-line"
        :style="{
          left: (level - 1) * 32 + 20 + 'px',
          height: '100%'
        }"
      ></div>
      <transition name="expand">
        <div
          v-if="
            expandedIds.includes(item.id) && item.reviewReplies && item.reviewReplies.length > 0
          "
          class="children-container"
        >
          <ReviewTree
            :data="item.reviewReplies"
            :level="level + 1"
            :parent-name="item.userName"
            @reply="$emit('reply', $event)"
            @delete="$emit('delete', $event)"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { formatApiDate } from "@/utils/utils";
import { deleteReviewReply } from "@/api/goodsReview";
import { MessageBox } from "element-ui";

export default {
  name: "ReviewTree",
  components: {},
  props: {
    data: {
      type: Array,
      default: () => []
    },
    level: {
      type: Number,
      default: 0
    },
    parentName: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      expandedIds: []
    };
  },
  methods: {
    formatDate(date) {
      return formatApiDate(date);
    },
    toggleExpand(item) {
      if (item.reviewReplies && item.reviewReplies.length > 0) {
        const index = this.expandedIds.indexOf(item.id);
        if (index > -1) {
          this.expandedIds.splice(index, 1);
        } else {
          this.expandedIds.push(item.id);
        }
      }
    },
    async handleDelete(item) {
      try {
        await MessageBox.confirm("确定要删除这条评论回复吗？", "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning"
        });

        const response = await deleteReviewReply({ Id: item.id });
        if (response && response.bizcode === 100) {
          this.$message.success("删除成功");
          this.$emit("delete", item);
        } else {
          this.$message.error("删除失败");
        }
      } catch (error) {
        if (error !== "cancel") {
          this.$message.error("删除失败");
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.review-tree {
  position: relative;
  padding-left: 0;

  .tree-item {
    position: relative;
    margin-bottom: 16px;
    padding-left: 0;
    border-left: none;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .item-wrapper {
    display: flex;
    gap: 12px;
    background: #ffffff;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }

  .avatar-container {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

    .user-avatar {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .user-avatar-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 18px;
    }
  }

  .content-wrapper {
    flex: 1;
    min-width: 0;
  }

  .item-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .user-name {
    font-weight: 600;
    color: #303133;
    font-size: 14px;
  }

  .reply-prefix {
    color: #909399;
    font-size: 14px;
  }

  .date-row {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
  }

  .date {
    font-size: 12px;
    color: #909399;
    flex-shrink: 0;
  }

  .item-content {
    margin-bottom: 10px;
  }

  .content {
    color: #606266;
    font-size: 14px;
    line-height: 1.6;
    word-break: break-word;
    white-space: pre-wrap;
  }

  .item-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    padding-top: 8px;
    border-top: 1px solid #f2f6fc;
  }

  .expand-toggle {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #409eff;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #764ba2;
    }

    i {
      font-size: 12px;
    }
  }

  .delete-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #f56c6c;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #f78989;
    }

    i {
      font-size: 12px;
    }
  }

  .tree-line {
    position: absolute;
    top: 0;
    width: 2px;
    background: linear-gradient(to bottom, #e4e7ed, #c0c4cc);
    pointer-events: none;
  }

  .children-container {
    margin-top: 12px;
    padding-left: 16px;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 20px;
      width: 2px;
      height: calc(100% - 20px);
      background: linear-gradient(to bottom, #e4e7ed, transparent);
    }
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
